import React from 'react'
import { NumericFormat } from 'react-number-format'

import { Input } from '../Input'
import { Slider } from '../ShadcnSlider'

interface SliderWithInputProps {
  value?: number
  defaultValue?: number
  min?: number
  max?: number
  step?: number
  onChange?: (value: number) => void
  className?: string
  inputClassName?: string
  sliderClassName?: string
  disabled?: boolean
  label?: string
  inputWidth?: number
  showMinMaxLabels?: boolean
  tight?: boolean
}

const SliderWithInput: React.FC<SliderWithInputProps> = ({
  value,
  defaultValue,
  min = 0,
  max = 100,
  step = 1,
  onChange,
  className,
  inputClassName,
  sliderClassName,
  disabled = false,
  label,
  inputWidth,
  showMinMaxLabels = false,
  tight = false,
}) => {
  const [internalValue, setInternalValue] = React.useState<number>(
    value ?? defaultValue ?? min,
  )
  const [rawInputValue, setRawInputValue] = React.useState<string>(
    String(value ?? defaultValue ?? min),
  )

  // Adjust state during render when the controlled `value` prop changes, rather
  // than in an effect: an effect would render once with the stale value and then
  // immediately re-render, which is the cascading render the compiler flags.
  const [lastSyncedValue, setLastSyncedValue] = React.useState(value)
  if (typeof value === 'number' && value !== lastSyncedValue) {
    setLastSyncedValue(value)
    setInternalValue(value)
    setRawInputValue(String(value))
  }

  const handleSliderChange = (vals: number[]) => {
    const newValue = vals[0]
    setInternalValue(newValue)
    setRawInputValue(String(newValue))
    onChange?.(newValue)
  }

  const commitInputValue = () => {
    if (rawInputValue === '.') return
    let newValue = Number(rawInputValue)
    if (isNaN(newValue)) newValue = min
    newValue = Math.max(min, Math.min(max, newValue))
    setInternalValue(newValue)
    setRawInputValue(String(newValue))
    onChange?.(newValue)
  }

  const handleInputBlur = () => {
    commitInputValue()
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      commitInputValue()
    }
  }

  return (
    // `w-full` so the slider expands to fill its parent panel. Without it the
    // outer block collapses to its widest child (the label or input) and the
    // flex-1 slider track gets zero room.
    <div className={`w-full ${className ?? ''}`}>
      {label && (
        <label
          className={`block font-medium text-text ${tight ? 'text-xs mb-1' : 'text-sm leading-6 mb-2'}`}
        >
          {label}
        </label>
      )}
      <div className={`flex items-center ${tight ? 'gap-2' : 'gap-4'}`}>
        {showMinMaxLabels && (
          <span
            className={`text-xs text-muted-foreground text-right min-w-[2ch] ${tight ? 'mr-1' : 'mr-2'}`}
          >
            {min}
          </span>
        )}
        <Slider
          className={sliderClassName}
          min={min}
          max={max}
          step={step}
          value={[internalValue]}
          onValueChange={handleSliderChange}
          disabled={disabled}
          style={{ flex: 1 }}
        />
        {showMinMaxLabels && (
          <span
            className={`text-xs text-muted-foreground text-left min-w-[2ch] ${tight ? 'ml-1' : 'ml-2'}`}
          >
            {max}
          </span>
        )}
        <NumericFormat
          value={rawInputValue}
          thousandSeparator=","
          decimalSeparator="."
          allowNegative={false}
          allowLeadingZeros={true}
          customInput={Input}
          type="text"
          min={min}
          max={max}
          step={step}
          onValueChange={(values: { value: string }) =>
            setRawInputValue(values.value)
          }
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          className={inputClassName}
          disabled={disabled}
          style={{
            width: inputWidth ?? (tight ? 50 : 80),
          }}
        />
      </div>
    </div>
  )
}

export { SliderWithInput }
export default SliderWithInput
